// src/components/menus/QuotesPageSubMenu.tsx
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BeingsPageSubMenuButton, BackButton } from '../common/Buttons';
import { SubMenuContainer, SubMenuCap } from './shared/MenuElements';
import { usePermissions } from '../../hooks/usePermissions';

interface QuotesPageSubMenuProps {
  bookingId: number;
  clientId?: number;
  clientIsProspect?: boolean;
  // True when the loaded quote has been converted to a real booking.
  // Disables (rather than hides) the EDIT tab so the operator sees the
  // tab exists but is no longer actionable - the retained quote is a
  // historical record and the backend rejects edits with 400.
  isConverted?: boolean;
  showBackButton?: boolean;
  bottomBorder?: boolean;
  buttonBottomBorder?: boolean;
  backButtonMode?: 'browser' | 'custom' | 'callback';
  customBackPath?: string;
  backSteps?: number;
  onBack?: () => void;
}

const QuotesPageSubMenu: React.FC<QuotesPageSubMenuProps> = ({
  bookingId,
  clientId,
  clientIsProspect = true,
  isConverted = false,
  showBackButton = true,
  bottomBorder = true,
  buttonBottomBorder = false,
  backButtonMode = 'browser',
  customBackPath,
  backSteps = -1,
  onBack,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canEditBookings } = usePermissions();

  const isEditActive = location.pathname.endsWith('/edit');
  const isViewActive = !isEditActive;

  const handleViewClick = () => navigate(`/quote/${bookingId}`);
  const handleEditClick = () => navigate(`/quote/${bookingId}/edit`);
  const handleClientClick = () => {
    if (clientId) navigate(`/clients/${clientId}`);
  };

  const handleBackClick = () => {
    if (onBack && backButtonMode === 'callback') {
      onBack();
      return;
    }
    if (backButtonMode === 'custom' && customBackPath) {
      navigate(customBackPath);
      return;
    }
    window.history.go(backSteps);
  };

  const menuItems = [
    {
      id: 'view',
      label: 'VIEW',
      onClick: handleViewClick,
      isActive: isViewActive,
      minWidth: '260px',
      visible: true,
      disabled: false,
    },
    {
      id: 'edit',
      label: 'EDIT',
      onClick: handleEditClick,
      isActive: isEditActive,
      minWidth: '110px',
      visible: canEditBookings,
      disabled: isConverted,
    },
    {
      id: 'client',
      label: 'CLIENT DETAILS',
      onClick: handleClientClick,
      isActive: false,
      minWidth: '268px',
      visible: !clientIsProspect && !!clientId,
      disabled: false,
    },
  ];

  return (
    <SubMenuContainer bottomBorder={bottomBorder}>
      {menuItems
        .filter((item) => item.visible)
        .map((item) => (
          <BeingsPageSubMenuButton
            key={item.id}
            onClick={item.onClick}
            isActive={item.isActive}
            minWidth={item.minWidth}
            showBottomBorder={buttonBottomBorder}
            disabled={item.disabled}
          >
            {item.label}
          </BeingsPageSubMenuButton>
        ))}

      {showBackButton && (
        <BackButton
          onClick={handleBackClick}
          navigationMode={
            backButtonMode === 'callback' ? 'custom' : backButtonMode
          }
          customPath={customBackPath}
          steps={backSteps}
        />
      )}
      <SubMenuCap />
    </SubMenuContainer>
  );
};

export default QuotesPageSubMenu;
